/** ActorTraits (Foundry: rule-element/actor-traits.ts): add or remove creature traits, with the roll options that follow. */
import { resolveInjectedProperties } from "../resolve";
import type { EngineState } from "../state";
import type { RuleHandler, RuleInstance } from "./base";

interface ActorTraitsData {
  add: string[];
  remove: string[];
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const list = (value: unknown, field: string): string[] | null => {
    if (value === undefined) return [];
    if (!Array.isArray(value) || !value.every((t) => typeof t === "string" && t)) {
      rule.fail(`${field}: must be an array of non-empty strings`);
      return null;
    }
    return value as string[];
  };
  const add = list(s.add, "add");
  const remove = list(s.remove, "remove");
  if (!add || !remove) return;
  if (typeof s.priority !== "number") rule.priority = 51;
  rule.data = { add, remove } satisfies ActorTraitsData as unknown as Record<string, unknown>;
}

function refreshMode(state: EngineState): void {
  const traits = state.actor.system.traits.value;
  for (const mode of ["construct", "living", "undead"]) state.rollOptions.delete("all", `self:mode:${mode}`);
  const mode = traits.includes("undead") ? "undead" : traits.includes("construct") ? "construct" : "living";
  state.rollOptions.set("all", `self:mode:${mode}`);
}

function onApplyActiveEffects(rule: RuleInstance, state: EngineState): void {
  if (!rule.test(state)) return;
  const data = rule.data as unknown as ActorTraitsData;
  const traits = state.actor.system.traits;
  const dictionary = state.catalogue?.table("creatureTraits");
  const newTraits = resolveInjectedProperties(rule, state, [...data.add]).filter((t) => !traits.value.includes(t));
  if (rule.ignored) return;
  for (const trait of newTraits) {
    if (dictionary && !(trait in dictionary)) return rule.fail(`${trait} is not a recognized trait`);
    traits.value.push(trait);
    state.rollOptions.set("all", `self:trait:${trait}`);
    if (trait === "construct" || trait === "undead") refreshMode(state);
  }
  for (const trait of resolveInjectedProperties(rule, state, [...data.remove])) {
    traits.value = traits.value.filter((t) => t !== trait);
    state.rollOptions.delete("all", `self:trait:${trait}`);
    if (trait === "construct" || trait === "undead") refreshMode(state);
  }
}

export const ActorTraitsHandler: RuleHandler = { key: "ActorTraits", parse, onApplyActiveEffects };
